import {
  Box,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  InputLabel,
  Divider,
  Table,
  TableFooter,
} from '@mui/material';
import { useState, useMemo, useEffect } from 'react';
import { useAppDispatch, useAppSelector } from '~/redux/hooks';

import { formatPercent, handleApiError } from '~/utils/helper';
import { IProjectReport } from '~/utils/interface/report';
import { HeadCellType } from '~/utils/types/common';
import { useTranslation } from 'react-i18next';
import { BaseDatePicker, BaseHeaderTitle, BaseRow, BaseSelect } from '~/components/common';
import dayjs from 'dayjs';
import { handleGetProjectReport } from '~/thunks/report/reportThunk';
import { handleGetLocations } from '~/thunks/location/locationThunk';
import { handleGetMasterData } from '~/thunks/master/masterThunk';
import { MASTER_DATA_JOB_TYPES } from '~/utils/constants/master';

const ProjectReport = () => {
  const { t } = useTranslation('admin/report');
  const dispatch = useAppDispatch();

  const currentDate = new Date();
  const end = currentDate.toISOString().split('T')[0];
  const start = new Date(currentDate.setMonth(currentDate.getMonth() - 1)).toISOString().split('T')[0];
  const { jobTypes } = useAppSelector((state) => state.master);

  // Dynamic columns for job types
  const headCells: Array<HeadCellType> = useMemo(
    () => [
      { id: 'location', label: t('properties.location') },
      ...(jobTypes?.map((type) => ({
        id: `${type.id}`,
        label: `${type.name} Projects`,
      })) || []),

      { id: 'total', label: t('properties.total') },
      { id: 'prjWon', label: t('properties.prjWon') },
      { id: 'prjLost', label: t('properties.prjLost') },
      { id: 'closingRate', label: t('properties.closingRate') },
      // Add dynamic job types columns
    ],
    [t, jobTypes]
  );

  const { locations } = useAppSelector((state) => state.location);

  const [startDate, setStartDate] = useState<string>(dayjs().format('YYYY-MM-DD'));
  const [endDate, setEndDate] = useState<string>(dayjs().format('YYYY-MM-DD'));

  const [selectedAccount, setSelectedAccount] = useState<Array<string>>([]); // State for selected account
  const [job, setJob] = useState<Array<IProjectReport>>([]);
  const optionsWithAll = [{ id: 'all', name: 'All' }, ...locations];

  const handleSearch = () => {
    if (selectedAccount.includes('all')) {
      dispatch(handleGetProjectReport({ startDate, endDate }))
        .unwrap()
        .then((response) => {
          setJob(response.result.report || []);
        })
        .catch(handleApiError);
    } else {
      dispatch(handleGetProjectReport({ startDate, endDate, createdBy: selectedAccount ?? [] }))
        .unwrap()
        .then((response) => {
          setJob(response.result.report || []);
        })
        .catch(handleApiError);
    }
  };

  useEffect(() => {
    dispatch(
      handleGetMasterData({
        type: [MASTER_DATA_JOB_TYPES],
      })
    );
  }, [dispatch]);

  useEffect(() => {
    dispatch(handleGetLocations({}))
      .unwrap()
      .catch((e) => {
        handleApiError(e);
      });
  }, [dispatch]);

  useEffect(() => {
    dispatch(handleGetProjectReport({ startDate: start, endDate: end }))
      .unwrap()
      .then((response) => {
        setStartDate(start);
        setEndDate(end);

        setSelectedAccount(['all']);
        setJob(response.result.report || []);
      })
      .catch(handleApiError);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Box>
      <BaseHeaderTitle title={t('title.project')} />

      <Divider className='mb-[20px] mt-[20px]' />
      <BaseRow className='gap-[20px] '>
        <Box className='w-[900px]'>
          <InputLabel>{t('properties.startDate')}</InputLabel>
          <BaseDatePicker
            value={startDate}
            onChange={(value) => setStartDate(dayjs(value).format('YYYY-MM-DD'))}
            dateFormat='DD MMM, YYYY'
          />
        </Box>
        <Box className='w-[900px]'>
          <InputLabel>{t('properties.endDate')}</InputLabel>
          <BaseDatePicker
            value={endDate}
            onChange={(value) => setEndDate(dayjs(value).format('YYYY-MM-DD'))}
            dateFormat='DD MMM, YYYY'
          />
        </Box>
        <Box className='w-full'>
          <InputLabel>{t('properties.location')}</InputLabel>
          <BaseSelect
            options={optionsWithAll}
            value={selectedAccount}
            onChange={(event) => {
              const selectedValue = event.target.value;

              if (selectedValue.includes('all') && !selectedAccount.includes('all')) {
                setSelectedAccount(['all']);
              } else if (selectedValue !== 'all' && selectedAccount.includes('all')) {
                setSelectedAccount(selectedValue.filter((value: any) => value !== 'all'));
              } else {
                setSelectedAccount(selectedValue);
              }
            }}
            label={t('properties.location')}
            placeholder={t('properties.location')}
            multiple
          />
        </Box>
        <Box className='w-[200px] mt-[12px]'>
          <InputLabel></InputLabel>
          <Button variant='contained' color='primary' onClick={handleSearch}>
            {t('search')}
          </Button>
        </Box>
      </BaseRow>

      <Divider className='mb-[20px] mt-[20px]' />
      <TableContainer className='max-h-[640px] overflow-auto'>
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              {headCells.map((cell) => (
                <TableCell key={cell.id} className='w-[9.09%] bg-[#F0F7FF] text-[#758CA5] text-sm font-bold'>
                  {cell.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {job.map((row, index) => (
              <TableRow key={index}>
                <TableCell className='w-[9.09%]'>{row.location}</TableCell>
                {jobTypes?.map((type) => (
                  <TableCell key={type.id} className='w-[9.09%]'>
                    {row[`${type.name}`] || 0}
                  </TableCell>
                ))}
                <TableCell className='w-[9.09%]'>{row.total}</TableCell>
                <TableCell className='w-[9.09%]'>{row.projectWon}</TableCell>
                <TableCell className='w-[9.09%]'>{row.projectLost}</TableCell>
                <TableCell className='w-[9.09%]'>{formatPercent(row.closingRate)}</TableCell>
                {/* Render job types dynamically */}
              </TableRow>
            ))}
          </TableBody>
          {/* Total row */}
          <TableFooter>
            <TableRow className='sticky bottom-0 bg-[#F0F7FF]'>
              {/* Empty cell for location */}
              <TableCell className='w-[9.09%] font-bold text-[#758CA5]'>
                {job.filter((row) => row.location).length}
              </TableCell>
              {/* Totals for each numeric column */}
              {jobTypes?.map((type) => (
                <TableCell key={type.id} className='w-[9.09%] font-bold text-[#758CA5]'>
                  {job.reduce((sum, row) => sum + (row[`${type.name}`] || 0), 0)}
                </TableCell>
              ))}
              <TableCell className='w-[9.09%] font-bold text-[#758CA5]'>
                {job.reduce((sum, row) => sum + (row.total || 0), 0)}
              </TableCell>
              <TableCell className='w-[9.09%] font-bold text-[#758CA5]'>
                {job.reduce((sum, row) => sum + (row.projectWon || 0), 0)}
              </TableCell>
              <TableCell className='w-[9.09%] font-bold text-[#758CA5]'>
                {job.reduce((sum, row) => sum + (row.projectLost || 0), 0)}
              </TableCell>
              <TableCell className='w-[9.09%] font-bold text-[#758CA5]'>
                {formatPercent(job.reduce((sum, row) => sum + (row.closingRate || 0), 0) / job.length)}
              </TableCell>
              {/* Add total for dynamic job types */}
            </TableRow>
          </TableFooter>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default ProjectReport;
